import React, {useCallback} from "react";
import {useDropzone} from "react-dropzone";
import {fromEvent} from "file-selector";
import {
    DEFAULT_IMAGES_ACCEPTABLE_EXTENSIONS,
    MAXIMUM_DOCUMENT_UPLOAD_SIZE,
    REACT_DROPZONE_ONE_MEGABYTE
} from "../../../util/util-constants"
import {pushNotification} from "../../../data/actions/ui";
import {useDispatch} from "react-redux";

export default function DropZoneAlt({
                                        content,
                                        maxFilesAccepted = 1,
                                        onAcceptFiles,
                                        accept = DEFAULT_IMAGES_ACCEPTABLE_EXTENSIONS,
                                        className = "",
                                        onDragLeave,
                                        maxSize = MAXIMUM_DOCUMENT_UPLOAD_SIZE
                                    }) {
    const dispatch = useDispatch();

    // Chrome returns empty string for DataTransferItem.type during dragenter/dragover
    // (unlike Firefox which returns "application/x-moz-file", already handled by react-dropzone).
    // When type is empty we fall back to the first accepted MIME type so drag-over shows
    // accept state. Actual validation still runs on drop via the normal flow.
    const getFilesFromEvent = useCallback(async (event) => {
        if (event.type !== 'drop' && event.dataTransfer && event.dataTransfer.items) {
            const fileItems = Array.from(event.dataTransfer.items).filter(i => i.kind === 'file');
            if (fileItems.length > 0) {
                const fallbackType = accept.split(',')[0].trim();
                return fileItems.map(item => ({
                    type: item.type || fallbackType,
                    name: ''
                }));
            }
        }
        return fromEvent(event);
    }, [accept]);

    const onDrop = useCallback(acceptedFiles => {
        if (acceptedFiles.length) {
            onAcceptFiles(acceptedFiles);
        }
    }, [])

    const onDropRejected = useCallback(rejectedFiles => {
        if (rejectedFiles.length) {
            let filesOverMaxSize = [];
            rejectedFiles.forEach(rejectedFile => {
                if (rejectedFile.size > maxSize) {
                    filesOverMaxSize.push(rejectedFile.name);
                }
            });

            if (filesOverMaxSize.length) {
               dispatch(pushNotification({
                        title: `One or more files are too large to upload (The limit is ${maxSize / REACT_DROPZONE_ONE_MEGABYTE}MB per file.)`,
                        content: "Rejected files: " + filesOverMaxSize.join(", "),
                        messageType: "error"
                    }
                ));
            } else {
                dispatch(pushNotification({
                        title: `Upload rejected: file does not meet requirements.`,
                        messageType: "error"
                    }
                ));
            }
        }
    }, [])

    const {
        getRootProps,
        getInputProps,
        isDragAccept,
        isDragReject,
    } = useDropzone({
        multiple: maxFilesAccepted > 1,
        maxFiles: maxFilesAccepted,
        onDropRejected: onDropRejected,
        onDrop,
        accept: accept,
        maxSize: maxSize,
        getFilesFromEvent
    });

    const rootProps = getRootProps();

    return (
        <div
            className={className}
            {...rootProps}
            onDragLeave={(e) => {
                rootProps.onDragLeave?.(e);
                onDragLeave?.(e);
            }}
        >
            <input {...getInputProps()} />

            {content(isDragAccept, isDragReject)}
        </div>
    )
}